import {
    IonButton, 
    IonCard, 
    IonCardContent, 
    IonCardTitle, 
    IonCol, 
    IonContent, 
    IonHeader,  
    IonIcon,  
    IonItem,  
    IonLabel,    
    IonPage, 
    IonRow, 
} from "@ionic/react";
import { callOutline, clipboardOutline, ellipsisVerticalCircleOutline, locationOutline, peopleOutline, phonePortraitOutline, ribbon } from "ionicons/icons";
import React, {useEffect, useState} from "react";
import { RouteComponentProps } from "react-router";
import EventDisplayContact from "../../components/Event/About/EventDisplayContact";
import EventDisplayDocuments from "../../components/Event/About/EventDisplayDocuments";
import EventDisplayLocation from "../../components/Event/About/EventDisplayLocation";
import EventDisplayOfficials from "../../components/Event/About/EventDisplayOfficials";
import DisplayScheduleWithClassInfo from "../../components/Event/Schedule/DisplayScheduleWithClassInfo";
import EventAuditorSignUpButton from "../../components/EventAuditor/EventAuditorSignUpButton";
import WebNavbar from "../../components/Navbars/WebNavbar";
import { Event } from "../../models";
import { formatShortAddress } from "../../utilities/address/FormatAddress";
import { formatStartEndDatesStrings } from "../../utilities/dates/FormatDates";
import { getEventById } from "../../utilities/events/Event";
import ErrorAlert from "../../components/Errors/ErrorAlert";
import EventDisplayJudges from "../../components/Event/About/EventDisplayJudges";
import constants from "../../constant/constant";
import EventDisplayWeblinks from "../../components/Event/About/EventDisplayWebLinks";

interface ViewShowPageProps extends RouteComponentProps<{
    id: string;
}> {}

const ViewShowPage: React.FC<ViewShowPageProps> = ({match}) => {
    const [currentEvent, setCurrentEvent] = useState<Event>();
    const [error, setError] = useState("");

    useEffect(() => {
        async function getEvent(id: string) {
            const queryResult = await getEventById(id);
            if (queryResult.isSuccess) {
                setCurrentEvent(queryResult.result);
            } else {
                setError("Sorry, a problem occurred. Please go back and try again.");
            }
        }

        if (match.params && match.params.id) {
            getEvent(match.params.id);
        } else {
            const path = match.path;
            const pathArray = path.split("/");
            const id = pathArray[pathArray.length-1];
            if (id) {
                getEvent(id);
            }
        }
    }, [match, match.params.id]);
    
    return (
        <IonPage id="viewShowPage" className="bg-light">
            <IonHeader>
                <WebNavbar />
            </IonHeader>
            <IonContent>
                {error && <ErrorAlert error={error} />}
                {currentEvent ? 
                    <>
                        {/* Basic Info */}
                        <IonRow className="ion-justify-content-center ion-padding-top">
                            <IonCol size="12" className="ion-text-center">
                                <h1>{currentEvent.name}</h1>
                                <h5>{currentEvent.address && formatShortAddress(currentEvent.address)} | {formatStartEndDatesStrings(currentEvent.startDate, currentEvent.endDate)}</h5>
                            </IonCol>
                        </IonRow>

                        {/* Sign Up Cards */}
                        <IonRow className="ion-justify-content-center">
                            {/* Text Alert Sign Up Card */}
                            {currentEvent.eventOptions?.usesTextAlerts && (
                                <IonCol sizeXs="12" sizeMd="6">
                                    <IonCard mode="md" className="ion-padding bg-white stretch">
                                        <IonCardTitle>
                                            <IonItem lines="none">
                                                <IonIcon icon={phonePortraitOutline} slot="start"/>
                                                <IonLabel> Text Alerts</IonLabel>
                                            </IonItem>
                                        </IonCardTitle>
                                        <IonCardContent>
                                            <IonRow className="ion-justify-content-center">
                                                <p>Click below to sign up to receive text messages about the show or adjust your alert settings.</p>
                                            </IonRow>
                                            <IonRow className="ion-justify-content-center">
                                                <EventAuditorSignUpButton event={currentEvent} />
                                            </IonRow>
                                        </IonCardContent>
                                    </IonCard>
                                </IonCol>
                            )}
                            {/* Online Entry Sign Up Card - check for Cedar Valley Riders org */}
                            {(currentEvent.eventOptions?.usesEventManagement && (currentEvent.organizationId !== constants.CEDAR_VALLEY_RIDERS_ORGANIZATION.id)) && (
                                <IonCol sizeXs="12" sizeMd="6">
                                    <IonCard mode="md" className="ion-padding bg-white stretch">
                                        <IonCardTitle>
                                            <IonItem lines="none">
                                                <IonIcon icon={clipboardOutline} slot="start"/>
                                                <IonLabel> Enter Online</IonLabel>
                                            </IonItem>
                                        </IonCardTitle>
                                        <IonCardContent>
                                            <IonRow className="ion-justify-content-center">
                                                <IonCol sizeXs="12" sizeMd="6" className="ion-text-center">
                                                    <p>Use your account information to quickly enter online.</p>
                                                    <a href="/login">
                                                        <IonButton color="tertiary">
                                                            Log In
                                                        </IonButton>
                                                    </a>
                                                </IonCol>
                                                <IonCol sizeXs="12" sizeMd="6" className="ion-text-center">
                                                    <p>Create an account to get started and create an entry.</p>
                                                    <a href="/register">
                                                        <IonButton color="tertiary">
                                                            Create Account
                                                        </IonButton>
                                                    </a>
                                                </IonCol>
                                            </IonRow>
                                        </IonCardContent>
                                    </IonCard>
                                </IonCol>
                            )}
                        </IonRow>

                        {/* General Event Info */}
                        <IonRow>
                            {/* Resources Card */}
                            <IonCol sizeSm="12" sizeMd="6">
                                <IonCard mode="md" className="ion-padding bg-white stretch">
                                    <IonCardTitle>
                                        <IonItem lines="none">
                                            <IonIcon icon={ellipsisVerticalCircleOutline} slot="start"/>
                                            <IonLabel> Resources</IonLabel>
                                        </IonItem>
                                    </IonCardTitle>
                                    <IonCardContent>
                                        <p>Documents:</p>
                                        <EventDisplayDocuments event={currentEvent} />
                                        <p className="pt-2">Links:</p>
                                        <EventDisplayWeblinks event={currentEvent} />
                                    </IonCardContent>
                                </IonCard>
                            </IonCol>

                            {/* Contact Card */}
                            <IonCol sizeSm="12" sizeMd="6">
                                <IonCard mode="md" className="ion-padding bg-white stretch">
                                    <IonCardTitle>
                                        <IonItem lines="none">
                                            <IonIcon icon={callOutline} slot="start"/>
                                            <IonLabel> Contact</IonLabel>
                                        </IonItem>
                                    </IonCardTitle>
                                    <IonCardContent>
                                        <EventDisplayContact event={currentEvent} />
                                    </IonCardContent>
                                </IonCard>
                            </IonCol>

                            {/* Officials Card */}
                            <IonCol sizeSm="12" sizeMd="6">
                                <IonCard mode="md" className="ion-padding bg-white stretch">
                                    <IonCardTitle>
                                        <IonItem lines="none">
                                            <IonIcon icon={peopleOutline} slot="start"/>
                                            <IonLabel> Officials</IonLabel>
                                        </IonItem>
                                    </IonCardTitle>
                                    <IonCardContent>
                                        <EventDisplayOfficials event={currentEvent} />
                                    </IonCardContent>
                                </IonCard>
                            </IonCol>

                            {/* Judges Card */}
                            <IonCol sizeSm="12" sizeMd="6">
                                <IonCard mode="md" className="ion-padding bg-white stretch">
                                    <IonCardTitle>
                                        <IonItem lines="none">
                                            <IonIcon icon={ribbon} slot="start"/>
                                            <IonLabel> Judges</IonLabel>
                                        </IonItem>
                                    </IonCardTitle>
                                    <IonCardContent>
                                        <EventDisplayJudges event={currentEvent} />
                                    </IonCardContent>
                                </IonCard>
                            </IonCol>

                            {/* Location Card */}
                            <IonCol sizeSm="12" sizeMd="6">
                                <IonCard mode="md" className="ion-padding bg-white stretch">
                                    <IonCardTitle>
                                        <IonItem lines="none">
                                            <IonIcon icon={locationOutline} slot="start"/>
                                            <IonLabel> Location</IonLabel>
                                        </IonItem>
                                    </IonCardTitle>
                                    <IonCardContent>
                                        <EventDisplayLocation event={currentEvent} />
                                    </IonCardContent>
                                </IonCard>
                            </IonCol>
                        </IonRow>

                        {/* Schedule Card */}
                        {currentEvent.eventOptions?.usesEventManagement && (
                            <IonRow className="mb-5">
                                <IonCol sizeSm="12" sizeMd="12">
                                    <DisplayScheduleWithClassInfo event={currentEvent}/>
                                </IonCol>
                            </IonRow>
                        )}
                    </>
                    :
                    <>  
                        <IonRow className="ion-justify-content-center">
                            <IonCol size="12" className="ion-text-center">
                                Loading...
                            </IonCol>
                        </IonRow>
                    </>
                }
            </IonContent>
        </IonPage>
    );
};

export default ViewShowPage;
